import React from 'react';
import { Box, Container, Typography, Paper, Divider, Link } from '@mui/material';
import i18next from '../../utilis/i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';

const PrivacyPolicyPage: React.FC = () => {
  useDocumentTitle(i18next.t('Views.PrivacyPolicyPage.Title'));

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          backgroundColor: 'rgba(18, 18, 18, 0.9)',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          borderRadius: '8px',
        }}
      >
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Title')}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {i18next.t('Views.PrivacyPolicy.LastUpdated', { date: 'April 2, 2025' })}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4, backgroundColor: 'rgba(212, 175, 55, 0.3)' }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Introduction.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Introduction.Content1')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Introduction.Content2')}
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.CollectedInfo.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.CollectedInfo.Description')}
          </Typography>
          <Typography variant="body1" component="div" sx={{ color: '#fff', mb: 2 }}>
            <ul>
              <li>{i18next.t('Views.PrivacyPolicy.CollectedInfo.List.Personal')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.CollectedInfo.List.Contact')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.CollectedInfo.List.Technical')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.CollectedInfo.List.Usage')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.CollectedInfo.List.Marketing')}</li>
            </ul>
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.HowWeUse.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.HowWeUse.Description')}
          </Typography>
          <Typography variant="body1" component="div" sx={{ color: '#fff', mb: 2 }}>
            <ul>
              <li>{i18next.t('Views.PrivacyPolicy.HowWeUse.List.Services')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.HowWeUse.List.Improve')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.HowWeUse.List.Communication')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.HowWeUse.List.Marketing')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.HowWeUse.List.Legal')}</li>
            </ul>
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.DataSharing.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.DataSharing.Description')}
          </Typography>
          <Typography variant="body1" component="div" sx={{ color: '#fff', mb: 2 }}>
            <ul>
              <li>{i18next.t('Views.PrivacyPolicy.DataSharing.List.ServiceProviders')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.DataSharing.List.BusinessPartners')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.DataSharing.List.LegalRequirements')}</li>
            </ul>
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.DataSecurity.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.DataSecurity.Content')}
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.YourRights.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.YourRights.Description')}
          </Typography>
          <Typography variant="body1" component="div" sx={{ color: '#fff', mb: 2 }}>
            <ul>
              <li>{i18next.t('Views.PrivacyPolicy.YourRights.List.Access')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.YourRights.List.Correction')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.YourRights.List.Deletion')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.YourRights.List.Restriction')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.YourRights.List.DataPortability')}</li>
              <li>{i18next.t('Views.PrivacyPolicy.YourRights.List.Objection')}</li>
            </ul>
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Cookies.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Cookies.Content')}
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.ThirdPartyLinks.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.ThirdPartyLinks.Content')}
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.ChildrenPrivacy.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.ChildrenPrivacy.Content')}
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Changes.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Changes.Content')}
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ color: '#D4AF37', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Contact.Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
            {i18next.t('Views.PrivacyPolicy.Contact.Content')}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <RouterLink to="/" style={{ textDecoration: 'none' }}>
            <Link component="button" sx={{ color: '#D4AF37' }}>
              {i18next.t('Views.PrivacyPolicy.BackToHome')}
            </Link>
          </RouterLink>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicyPage;
